import { AibTransactionResultEnum, AibTransactionStateEnum, AibTransactionTypeEnum } from '../../enum/payment-aib';

/**
 * Represents the components of a monetary amount.
 */
interface AmountComponents {
  subtotal: number;
  // Add other components like tax, shipping, etc., if they can appear.
}

/**
 * Represents a monetary amount with its currency and components.
 */
interface Amount {
  total: number;
  currency: string;
  components: AmountComponents;
}

/**
 * Contains details about the payment card used in the transaction.
 */
interface PaymentCard {
  expiryDate: {
    month: string;
    year: string;
  };
  bin: string;
  last4: string;
  brand: string;
}

/**
 * Encapsulates the details of the payment method.
 */
interface PaymentMethodDetails {
  paymentCard: PaymentCard;
  paymentMethodType: 'PAYMENT_CARD' | unknown; // Literal type for better type safety
  paymentMethodBrand: string;
}

/**
 * Represents a tokenized version of the payment method.
 */
interface PaymentToken {
  last4: string;
  brand: string;
}

/**
 * Contains the Address Verification Service (AVS) response details.
 */
interface AvsResponse {
  streetMatch: string;
  postalCodeMatch: string;
}

/**
 * Holds details from the payment processor.
 */
interface Processor {
  referenceNumber: string;
  authorizationCode: string;
  responseCode: string;
  responseMessage: string;
  avsResponse: AvsResponse;
  securityCodeResponse: 'MATCHED' | 'NOT_MATCHED' | unknown;
}

/**
 * Container for any additional, non-standard response data.
 */
interface AdditionalDetails {
  // Using a record type allows for flexible, yet typed, additional data.
  additionalResponseData: Record<string, unknown>;
}

/**
 * The main interface for the entire transaction response object.
 */
export interface AibTransactionStatusResponse {
  type: 'transactionResponse';
  clientRequestId: string;
  apiTraceId: string;
  ipgTransactionId: string;
  orderId: string;
  transactionType: AibTransactionTypeEnum;
  paymentToken: PaymentToken;
  transactionOrigin: 'ECOM' | 'ECI' ;
  paymentMethodDetails: PaymentMethodDetails;
  country: string;
  terminalId: string;
  merchantId: string;
  transactionTime: number; // Unix timestamp
  approvedAmount: Amount;
  transactionAmount: Amount;

  // https://docs.fiserv.dev/public/docs/transaction-result-analysis
  // APPROVED in case of an approval or
  // WAITING in case the final result is not yet clear and will be updated at a later point.
  // DECLINED is returned in case the credit card processor does not accept the transaction, e.g. when finding the customer’s funds not to be sufficient.
  // FRAUD is returned in case a fraud attempt is assumed by the Gateway.
  // If an internal gateway error should occur, the returned value is FAILED.
  transactionResult: AibTransactionResultEnum; // 'APPROVED' | 'WAITING' | 'DECLINED' | 'FRAUD' | 'FAILED';
  approvalCode: string;
  transactionState: AibTransactionStateEnum; // 'CAPTURED' | 'AUTHORIZED' | 'DECLINED' | 'VOIDED' | 'SETTLED';
  transactionStatus?: string;
  schemeTransactionId: string;
  processor: Processor;
  additionalDetails: AdditionalDetails;
}

/**
 * The main interface for a transaction-specific error response.
 */
export interface TransactionErrorResponse {
  type: 'TransactionErrorResponse';
  clientRequestId: string;
  apiTraceId: string;
  responseType: string;
  ipgTransactionId: string;
  orderId: string;
  transactionType: string;
  paymentToken: TransactionErrorPaymentToken;
  transactionOrigin: string;
  paymentMethodDetails: PaymentMethodDetails;
  transactionTime: number;
  transactionAmount: Partial<Amount>; // Use Partial to allow for missing currency/components
  transactionStatus: string;
  approvalCode: string;
  errorMessage: string;
  additionalDetails: AdditionalDetails;
  error: SimpleError;
}

interface SimpleError {
  code: string;
  message: string;
}

interface TransactionErrorPaymentToken {
  reusable?: boolean;
  declineDuplicates?: boolean;
  type?: string;
}